import React, { useEffect, useState } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Breadcrumbs from '../components/Breadcrumbs';
import WhatsAppWidget from '../components/WhatsAppWidget';
import keywordPages from '../data/keywordPages.json';
import { ArrowRight, CheckCircle2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const formatPrice = (p) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(p);

export default function KeywordLandingPage({ slug }) {
  const [scrolled, setScrolled] = useState(false);
  const [products, setProducts] = useState([]);
  const data = keywordPages.find((k) => k.slug === slug);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', onScroll);
    window.scrollTo(0, 0);
    fetch(`${API}/api/products`).then((r) => r.json()).then(setProducts).catch(() => {});
    return () => window.removeEventListener('scroll', onScroll);
  }, [slug]);

  if (!data) return <Navigate to="/" replace />;
  const recs = data.productSlugs.map((s) => products.find((p) => p.slug === s)).filter(Boolean);

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: data.faq.map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })),
  };

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>{data.title}</title>
        <meta name="description" content={data.metaDescription} />
        <link rel="canonical" href={`https://harpalyk.com/${data.slug}`} />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>
      <Header scrolled={scrolled} />
      <main className="pt-16 lg:pt-[72px]" data-testid={`keyword-page-${data.slug}`}>
        <Breadcrumbs items={[{ label: data.keyword }]} />

        <section className="py-16 lg:py-24 px-6 lg:px-12 bg-gradient-to-b from-[#F8F9FA] to-white">
          <div className="max-w-4xl mx-auto">
            <p className="text-xs font-semibold tracking-[0.25em] text-emerald-600 uppercase mb-4">Harpalyk Robotics</p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-gray-900 mb-8">{data.h1}</h1>
            {data.intro.map((p, i) => (
              <p key={i} className="text-base md:text-lg text-gray-600 leading-relaxed mb-5">{p}</p>
            ))}
            <a href="/#contact" data-testid="keyword-demo-cta" className="inline-flex items-center gap-2 rounded-full bg-gray-900 text-white px-8 py-4 text-base font-medium hover:bg-gray-700 transition-colors duration-300 mt-3">
              Book a Free Demo <ArrowRight size={17} />
            </a>
          </div>
        </section>

        <section className="py-12 lg:py-16 px-6 lg:px-12">
          <div className="max-w-4xl mx-auto space-y-12">
            {data.sections.map((s) => (
              <div key={s.h2}>
                <h2 className="font-heading text-2xl md:text-3xl font-semibold text-gray-900 mb-4">{s.h2}</h2>
                <p className="text-gray-600 leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </section>

        {recs.length > 0 && (
          <section className="py-12 lg:py-16 px-6 lg:px-12 bg-[#F8F9FA]">
            <div className="max-w-4xl mx-auto">
              <h2 className="font-heading text-2xl md:text-3xl font-semibold text-gray-900 mb-8">Recommended Models</h2>
              <div className="grid sm:grid-cols-2 gap-6">
                {recs.map((p) => (
                  <a key={p.slug} href={p.productPageLink || '/#products'} className="group rounded-3xl bg-white border border-black/5 p-6 hover:shadow-xl hover:-translate-y-1 transition-[box-shadow,transform] duration-500">
                    <div className="h-44 rounded-2xl overflow-hidden bg-[#F8F9FA] mb-5">
                      <img src={p.image} alt={p.name} loading="lazy" className="w-full h-full object-contain p-3 group-hover:scale-105 transition-transform duration-700" />
                    </div>
                    <h3 className="font-heading text-xl font-semibold text-gray-900 mb-1">{p.name}</h3>
                    <p className="text-lg font-bold text-gray-900 mb-2">{formatPrice(p.salePrice)}</p>
                    <span className="inline-flex items-center gap-1.5 text-sm font-medium text-emerald-600">
                      View Details <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform duration-300" />
                    </span>
                  </a>
                ))}
              </div>
            </div>
          </section>
        )}

        <section className="py-12 lg:py-20 px-6 lg:px-12">
          <div className="max-w-4xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-gray-900 mb-8">Frequently Asked Questions</h2>
            <div className="space-y-4">
              {data.faq.map((f) => (
                <div key={f.q} className="rounded-2xl border border-black/5 bg-[#F8F9FA] p-6">
                  <h3 className="font-heading text-base font-semibold text-gray-900 mb-2">{f.q}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{f.a}</p>
                </div>
              ))}
            </div>
            <div className="mt-10">
              <h3 className="font-heading text-sm font-semibold text-gray-900 mb-3">Related Resources</h3>
              <ul className="space-y-2">
                {data.links.map((l) => (
                  <li key={l.href} className="flex items-start gap-2 text-sm">
                    <CheckCircle2 size={15} className="text-emerald-600 mt-0.5" />
                    <Link to={l.href} className="text-emerald-700 hover:underline">{l.label}</Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <WhatsAppWidget />
    </div>
  );
}
